import type { Locale } from '../i18n/locale'
import type { FenceStyle } from '../calculators/fence'

// Side-elevation of ONE fence section: two posts (with caps), the chosen number
// of horizontal rails, and the infill boards drawn per style — spaced pointed
// pickets, solid dog-ear privacy boards, or no boards at all for split-rail.
// Everything scales with the inputs so the picture matches the numbers (wider
// pickets → fewer, fatter boards; 3 rails → an extra rail; etc.).

interface Props {
  style: FenceStyle
  railCount: number
  postSpacingFt: number
  picketWidthIn: number
  locale: Locale
}

const POST = '#166534'        // dark green — structural posts
const RAIL = '#22c55e'        // medium green — rails (show through picket gaps)
const BOARD_FILL = '#dcfce7'  // light green infill
const BOARD_STROKE = '#166534'
const GROUND = '#94a3b8'

export default function FenceDiagram({ style, railCount, postSpacingFt, picketWidthIn, locale }: Props) {
  const W = 320, H = 200
  const groundY = 172
  const postW = 11
  const leftPostX = 22
  const rightPostX = W - 22 - postW       // 287
  const postTopY = 46
  const capH = 6, capOverhang = 3
  const boardTopY = 52
  const boardBotY = 166
  const intoX = leftPostX + postW         // boards start just inside the left post
  const intoRight = rightPostX            // …and stop at the right post
  const interiorW = intoRight - intoX
  const fenceH = boardBotY - boardTopY

  // Rails evenly-ish spaced; 2 or 3.
  const n = Math.max(2, Math.min(3, Math.round(railCount)))
  const railYs = n === 2
    ? [boardTopY + fenceH * 0.2, boardTopY + fenceH * 0.72]
    : [boardTopY + fenceH * 0.1, boardTopY + fenceH * 0.46, boardTopY + fenceH * 0.82]
  const railTh = 7

  // Boards: real count per section so picket width reads true (capped for sanity).
  const showBoards = style !== 'split-rail'
  const secInches = Math.max(12, postSpacingFt * 12)
  const gapIn = style === 'privacy' ? 0 : 0.5
  const bwIn = Math.max(1, picketWidthIn)
  const pitchIn = bwIn + gapIn
  const count = showBoards ? Math.min(90, Math.max(1, Math.floor(secInches / pitchIn))) : 0
  const upi = interiorW / secInches
  const bwU = bwIn * upi
  const pitchU = pitchIn * upi

  const boardPoints = (x: number, w: number): string => {
    if (style === 'picket') {
      const peak = Math.min(12, fenceH * 0.12) // pointed picket top
      return `${x},${boardTopY + peak} ${x + w / 2},${boardTopY} ${x + w},${boardTopY + peak} ${x + w},${boardBotY} ${x},${boardBotY}`
    }
    const ch = Math.min(w * 0.35, 5) // dog-ear chamfer on privacy boards
    return `${x},${boardTopY + ch} ${x + ch},${boardTopY} ${x + w - ch},${boardTopY} ${x + w},${boardTopY + ch} ${x + w},${boardBotY} ${x},${boardBotY}`
  }

  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full" role="img" aria-label={`${style} fence section: ${n} rails, ${postSpacingFt} ft spacing`}>
      {/* Ground line */}
      <line x1={8} y1={groundY} x2={W - 8} y2={groundY} stroke={GROUND} strokeWidth={1.5} />

      {/* Rails behind the boards (visible through picket gaps / the only feature on split-rail) */}
      {railYs.map((y, k) => (
        <rect key={`rail${k}`} x={leftPostX} y={y - railTh / 2} width={rightPostX + postW - leftPostX} height={railTh} fill={RAIL} rx={1.5} />
      ))}

      {/* Infill boards */}
      {showBoards && Array.from({ length: count }, (_, k) => {
        const x = intoX + k * pitchU
        if (x + bwU > intoRight + 0.5) return null
        return <polygon key={`board${k}`} points={boardPoints(x, bwU)} fill={BOARD_FILL} stroke={BOARD_STROKE} strokeWidth={0.8} strokeLinejoin="round" />
      })}

      {/* Posts + caps drawn last so they frame the section cleanly */}
      {[leftPostX, rightPostX].map((x, k) => (
        <g key={`post${k}`}>
          <rect x={x} y={postTopY} width={postW} height={groundY - postTopY} fill={POST} />
          <rect x={x - capOverhang} y={postTopY - capH} width={postW + capOverhang * 2} height={capH} fill={POST} rx={1.5} />
        </g>
      ))}
    </svg>
  )
}
